import type { EnhancedUnifiedFormData } from '@/validation/enhancedUnifiedSchemas';
import type { HealthScoreBreakdown, HealthScoreComponent, ActionItem } from '@/types/supremeIntelligenceTypes';

/**
 * Calculate comprehensive financial health score (0-100)
 */
export const calculateFinancialHealthScore = (
  formData: EnhancedUnifiedFormData,
  mortgagePayment: number
): HealthScoreBreakdown => {
  // Calculate component scores
  const creditScoreComponent = calculateCreditScoreComponent(formData.personalDetails.creditScore);
  const dtiComponent = calculateDTIComponent(formData, mortgagePayment);
  const savingsComponent = calculateSavingsRateComponent(formData, mortgagePayment);
  const emergencyFundComponent = calculateEmergencyFundComponent(formData, mortgagePayment);
  const debtLevelComponent = calculateDebtLevelComponent(formData);

  // Calculate total weighted score
  const totalScore = Math.round(
    creditScoreComponent.score * (creditScoreComponent.weight / 100) +
    dtiComponent.score * (dtiComponent.weight / 100) +
    savingsComponent.score * (savingsComponent.weight / 100) +
    emergencyFundComponent.score * (emergencyFundComponent.weight / 100) +
    debtLevelComponent.score * (debtLevelComponent.weight / 100)
  );

  // Determine grade and color
  const { grade, color } = getGradeFromScore(totalScore);

  // Analyze strengths and weaknesses
  const components = {
    creditScore: creditScoreComponent,
    dtiRatio: dtiComponent,
    savingsRate: savingsComponent,
    emergencyFund: emergencyFundComponent,
    debtLevel: debtLevelComponent,
  };

  const strengths = identifyStrengths(components);
  const improvements = identifyImprovements(components);
  const actionItems = generateActionItems(components, formData);
  const projectedScore12Months = calculateProjectedScore(totalScore, actionItems);

  return {
    totalScore,
    grade,
    color,
    components,
    strengths,
    improvements,
    actionItems,
    projectedScore12Months,
  };
};

// Component scoring functions
const calculateCreditScoreComponent = (creditScore: number): HealthScoreComponent => {
  let score = 0;
  if (creditScore >= 740) score = 100;
  else if (creditScore >= 680) score = 80;
  else if (creditScore >= 620) score = 60;
  else if (creditScore >= 580) score = 40;
  else score = 0;

  return {
    score,
    weight: 25,
    value: creditScore,
    label: 'Credit Score',
  };
};

const calculateDTIComponent = (formData: EnhancedUnifiedFormData, mortgagePayment: number): HealthScoreComponent => {
  const monthlyIncome = (formData.primaryIncome.annualIncome + formData.primaryIncome.additionalIncome) / 12;
  const coBorrowerIncome = formData.coBorrowerIncome.enabled ? formData.coBorrowerIncome.additionalIncome / 12 : 0;
  const totalMonthlyIncome = monthlyIncome + coBorrowerIncome;

  const monthlyDebts = 
    formData.debts.autoLoanPayment +
    formData.debts.creditCardPayment +
    formData.debts.personalLoanPayment +
    formData.debts.childSupportPayment +
    formData.debts.otherDebts;

  const totalDebt = monthlyDebts + mortgagePayment;
  const dtiRatio = totalMonthlyIncome > 0 ? (totalDebt / totalMonthlyIncome) * 100 : 100;

  let score = 0;
  if (dtiRatio < 28) score = 100;
  else if (dtiRatio < 36) score = 80;
  else if (dtiRatio < 43) score = 60;
  else if (dtiRatio < 50) score = 40;
  else score = 0;

  return {
    score,
    weight: 25,
    value: `${dtiRatio.toFixed(1)}%`,
    label: 'DTI Ratio',
  };
};

const calculateSavingsRateComponent = (formData: EnhancedUnifiedFormData, mortgagePayment: number): HealthScoreComponent => {
  const monthlyIncome = (formData.primaryIncome.annualIncome + formData.primaryIncome.additionalIncome) / 12;
  const coBorrowerIncome = formData.coBorrowerIncome.enabled ? formData.coBorrowerIncome.additionalIncome / 12 : 0;
  const totalMonthlyIncome = monthlyIncome + coBorrowerIncome;

  const monthlyDebts = 
    formData.debts.autoLoanPayment +
    formData.debts.creditCardPayment +
    formData.debts.personalLoanPayment +
    formData.debts.childSupportPayment +
    formData.debts.otherDebts;

  const monthlyExpenses = mortgagePayment + monthlyDebts + (totalMonthlyIncome * 0.3); // Assume 30% for other expenses
  const monthlySavings = totalMonthlyIncome - monthlyExpenses;
  const savingsRate = totalMonthlyIncome > 0 ? (monthlySavings / totalMonthlyIncome) * 100 : 0;

  let score = 0;
  if (savingsRate > 20) score = 100;
  else if (savingsRate > 15) score = 90;
  else if (savingsRate > 10) score = 75;
  else if (savingsRate > 5) score = 50;
  else score = 0;

  return {
    score,
    weight: 20,
    value: `${Math.max(0, savingsRate).toFixed(1)}%`,
    label: 'Savings Rate',
  };
};

const calculateEmergencyFundComponent = (formData: EnhancedUnifiedFormData, mortgagePayment: number): HealthScoreComponent => {
  const monthlyIncome = (formData.primaryIncome.annualIncome + formData.primaryIncome.additionalIncome) / 12;
  const coBorrowerIncome = formData.coBorrowerIncome.enabled ? formData.coBorrowerIncome.additionalIncome / 12 : 0;
  const totalMonthlyIncome = monthlyIncome + coBorrowerIncome;

  const monthlyExpenses = mortgagePayment + 
    formData.debts.autoLoanPayment +
    formData.debts.creditCardPayment +
    formData.debts.personalLoanPayment +
    formData.debts.childSupportPayment +
    formData.debts.otherDebts +
    (totalMonthlyIncome * 0.3);

  // Estimate emergency fund based on down payment savings
  const estimatedEmergencyFund = formData.propertyLoan.downPaymentAmount * 0.2; // Assume 20% of down payment is emergency fund
  const monthsCovered = monthlyExpenses > 0 ? estimatedEmergencyFund / monthlyExpenses : 0;

  let score = 0;
  if (monthsCovered > 9) score = 100;
  else if (monthsCovered > 6) score = 90;
  else if (monthsCovered > 3) score = 75;
  else if (monthsCovered > 1) score = 50;
  else score = 0;

  return {
    score,
    weight: 15,
    value: `${monthsCovered.toFixed(1)} months`,
    label: 'Emergency Fund',
  };
};

const calculateDebtLevelComponent = (formData: EnhancedUnifiedFormData): HealthScoreComponent => {
  const annualIncome = formData.primaryIncome.annualIncome + formData.primaryIncome.additionalIncome;
  const coBorrowerIncome = formData.coBorrowerIncome.enabled ? formData.coBorrowerIncome.additionalIncome : 0;
  const totalAnnualIncome = annualIncome + coBorrowerIncome;

  const totalDebt = 
    (formData.debts.autoLoanPayment * 12) +
    (formData.debts.creditCardPayment * 12) +
    formData.debts.studentLoanBalance +
    (formData.debts.personalLoanPayment * 12) +
    (formData.debts.childSupportPayment * 12) +
    (formData.debts.otherDebts * 12);

  const debtToIncomeRatio = totalAnnualIncome > 0 ? (totalDebt / totalAnnualIncome) * 100 : 100;

  let score = 0;
  if (debtToIncomeRatio < 20) score = 100;
  else if (debtToIncomeRatio < 40) score = 70;
  else if (debtToIncomeRatio < 60) score = 40;
  else score = 0;

  return {
    score,
    weight: 15,
    value: `${debtToIncomeRatio.toFixed(1)}%`,
    label: 'Debt Level',
  };
};

// Helper functions
const getGradeFromScore = (score: number): { grade: HealthScoreBreakdown['grade']; color: string } => {
  if (score >= 90) return { grade: 'Excellent', color: 'hsl(var(--chart-2))' };
  if (score >= 80) return { grade: 'Very Good', color: 'hsl(var(--chart-3))' };
  if (score >= 70) return { grade: 'Good', color: 'hsl(var(--chart-4))' };
  if (score >= 60) return { grade: 'Fair', color: 'hsl(var(--chart-5))' };
  return { grade: 'Poor', color: 'hsl(var(--destructive))' };
};

const identifyStrengths = (components: HealthScoreBreakdown['components']): string[] => {
  const strengths: string[] = [];
  
  Object.entries(components).forEach(([key, component]) => {
    if (component.score >= 80) {
      const messages: Record<string, string> = {
        creditScore: `Excellent credit score of ${component.value}`,
        dtiRatio: `Healthy debt-to-income ratio at ${component.value}`,
        savingsRate: `Strong savings rate of ${component.value}`,
        emergencyFund: `Well-funded emergency fund covering ${component.value}`,
        debtLevel: `Low overall debt burden at ${component.value} of income`,
      };
      strengths.push(messages[key]);
    }
  });

  return strengths.length > 0 ? strengths : ['Building financial foundation'];
};

const identifyImprovements = (components: HealthScoreBreakdown['components']): string[] => {
  const improvements: string[] = [];
  
  Object.entries(components).forEach(([key, component]) => {
    if (component.score < 70) {
      const messages: Record<string, string> = {
        creditScore: 'Credit score could be improved',
        dtiRatio: 'Debt-to-income ratio is higher than ideal',
        savingsRate: 'Savings rate should be increased',
        emergencyFund: 'Emergency fund needs strengthening',
        debtLevel: 'Overall debt burden should be reduced',
      };
      improvements.push(messages[key]);
    }
  });

  return improvements;
};

const generateActionItems = (
  components: HealthScoreBreakdown['components'],
  formData: EnhancedUnifiedFormData
): ActionItem[] => {
  const actions: ActionItem[] = [];

  if (components.creditScore.score < 80) {
    actions.push({
      priority: 'high',
      action: 'Pay down credit card balances to below 30% utilization',
      impact: '+20-40 points credit score',
      timeframe: '2-3 months',
    });
  }

  if (components.dtiRatio.score < 70) {
    actions.push({
      priority: 'high',
      action: 'Reduce monthly debt payments by 10-15%',
      impact: 'Lower DTI by 5-8%',
      timeframe: '3-6 months',
    });
  }

  if (components.savingsRate.score < 70) {
    actions.push({
      priority: 'medium',
      action: 'Increase monthly savings by redirecting 5% of income',
      impact: 'Build emergency fund faster',
      timeframe: '1-2 months',
    });
  }

  if (components.emergencyFund.score < 70) {
    actions.push({
      priority: 'high',
      action: 'Build emergency fund to cover 3-6 months of expenses',
      impact: 'Financial security',
      timeframe: '6-12 months',
    });
  }

  if (components.debtLevel.score < 70) {
    actions.push({
      priority: 'medium',
      action: 'Focus on paying off highest-interest debt first',
      impact: 'Reduce total debt by 15-20%',
      timeframe: '6-12 months',
    });
  }

  return actions.slice(0, 5); // Top 5 actions
};

const calculateProjectedScore = (currentScore: number, actionItems: ActionItem[]): number => {
  // Conservative projection: +1-2 points per high priority action, +0.5 per medium/low
  const highPriorityCount = actionItems.filter(a => a.priority === 'high').length;
  const mediumPriorityCount = actionItems.filter(a => a.priority === 'medium').length;
  
  const projectedIncrease = (highPriorityCount * 1.5) + (mediumPriorityCount * 0.75);
  return Math.min(100, Math.round(currentScore + projectedIncrease));
};
